import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, LayoutDashboard, Compass, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useLang, LangToggle } from "@/i18n/LanguageContext";
import { LogoMark, LogoWordmark } from "@/components/Logo";

export default function Navbar({ onEnquire }) {
  const { user, logout, isAdmin } = useAuth();
  const { count } = useCart();
  const { t } = useLang();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef(null);

  const LINKS = [
    { to: "/", label: t("nav.villa"), testid: "nav-villa" },
    { to: "/excursions", label: t("nav.experiences"), testid: "nav-excursions" },
    { to: "/restaurants", label: t("nav.dining"), testid: "nav-restaurants" },
    { to: "/services", label: t("nav.services"), testid: "nav-services" },
    { to: "/blog", label: t("nav.blog"), testid: "nav-blog" },
  ];

  useEffect(() => {
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setUserMenu(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const doLogout = async () => {
    await logout();
    setUserMenu(false);
    setMenuOpen(false);
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-sand/80 border-b border-terracotta/15" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-3 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <LogoMark size={38} />
          <LogoWordmark />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${isActive ? "text-terracotta" : "text-ink/70 hover:text-terracotta"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/cart" data-testid="nav-cart-btn" aria-label="Cart"
                className="relative p-2 text-ink/70 hover:text-terracotta transition-colors">
            <ShoppingBag size={19} />
            {count > 0 && (
              <span data-testid="nav-cart-badge"
                    className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta text-sand text-[10px] font-bold flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>
          <LangToggle />
          {user ? (
            <div className="relative" ref={menuRef}>
              <button
                data-testid="nav-user-menu-btn"
                onClick={() => setUserMenu((v) => !v)}
                className="flex items-center gap-2 rounded-full border border-ink/10 bg-white px-4 py-2 text-sm font-medium hover:border-terracotta transition-colors"
              >
                <span className="w-6 h-6 rounded-full bg-terracotta text-sand flex items-center justify-center text-xs font-bold">
                  {user.name[0].toUpperCase()}
                </span>
                {user.name.split(" ")[0]}
              </button>
              {userMenu && (
                <div className="absolute right-0 mt-2 w-52 rounded-2xl bg-white shadow-xl border border-ink/10 py-2" data-testid="nav-user-dropdown">
                  <Link to="/account" onClick={() => setUserMenu(false)} data-testid="nav-my-trips" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-sand transition-colors">
                    <Compass size={15} /> {t("nav.myTrips")}
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" onClick={() => setUserMenu(false)} data-testid="nav-admin" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-sand transition-colors">
                      <LayoutDashboard size={15} /> {t("nav.ownerPanel")}
                    </Link>
                  )}
                  <button onClick={doLogout} data-testid="nav-logout-btn" className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-terracotta hover:bg-sand transition-colors">
                    <LogOut size={15} /> {t("nav.signout")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/auth" data-testid="nav-signin-btn" className="text-sm font-semibold text-ink/70 hover:text-terracotta transition-colors flex items-center gap-1.5">
              <User size={16} /> {t("nav.signin")}
            </Link>
          )}
          <button
            data-testid="nav-enquire-btn"
            onClick={onEnquire}
            className="rounded-full bg-terracotta text-sand px-5 py-2.5 text-sm font-semibold hover:bg-terracotta-dark transition-colors"
          >
            {t("nav.enquire")}
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <Link to="/cart" data-testid="nav-cart-btn-mobile" aria-label="Cart"
                className="relative p-2 text-ink/70 hover:text-terracotta transition-colors">
            <ShoppingBag size={19} />
            {count > 0 && (
              <span data-testid="nav-cart-badge-mobile"
                    className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta text-sand text-[10px] font-bold flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>
          <LangToggle suffix="-mobile" />
          <button className="p-2" onClick={() => setMenuOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label="Toggle menu">
            {menuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-terracotta/10 bg-sand px-6 py-4 flex flex-col gap-1" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} onClick={() => setMenuOpen(false)} data-testid={`${l.testid}-mobile`} className="py-2.5 text-sm font-medium">
              {l.label}
            </NavLink>
          ))}
          {user ? (
            <>
              <Link to="/account" onClick={() => setMenuOpen(false)} data-testid="nav-my-trips-mobile" className="py-2.5 text-sm font-medium">
                {t("nav.myTrips")}
              </Link>
              {isAdmin && (
                <Link to="/admin" onClick={() => setMenuOpen(false)} data-testid="nav-admin-mobile" className="py-2.5 text-sm font-medium">
                  {t("nav.ownerPanel")}
                </Link>
              )}
              <button onClick={doLogout} data-testid="nav-logout-btn-mobile" className="py-2.5 text-sm font-medium text-terracotta text-left">
                {t("nav.signout")}
              </button>
            </>
          ) : (
            <Link to="/auth" onClick={() => setMenuOpen(false)} data-testid="nav-signin-btn-mobile" className="py-2.5 text-sm font-medium">
              {t("nav.signin")}
            </Link>
          )}
          <button
            data-testid="nav-enquire-btn-mobile"
            onClick={() => {
              setMenuOpen(false);
              onEnquire();
            }}
            className="mt-2 rounded-full bg-terracotta text-sand px-5 py-3 text-sm font-semibold"
          >
            {t("nav.enquireMobile")}
          </button>
        </div>
      )}
    </header>
  );
}
